import { useApolloClient } from "@apollo/client";
import { InfoLabelMessage } from "@msys/ui";
import { Button, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { LoginButton } from "../auth/LoginButton.js";
import { RegisterButton } from "../auth/RegisterButton.js";
import { INVITATION_SCOPE } from "../auth/signup/signupScope.js";
import { AvailableUser, SelectUserList } from "../auth/SelectUserList.js";
import { useSelectedUser } from "../auth/useSelectedUser.js";
import { SELECTED_USER_HTTP_HEADER } from "../constants.js";
import { getCrmLinkingInvitationPrefill } from "./crmLinkingInvitationPrefill.js";
import {
  ONBOARDING_OUTLINED_BUTTON_SX,
  ONBOARDING_PRIMARY_BUTTON_SX,
} from "../features/organisations/onboarding/onboardingDesign.js";
import { ButtonDivider } from "./ButtonDivider.js";
import { ContinueOnAnotherDeviceSection } from "./ContinueOnAnotherDeviceSection.js";
import { useAcceptCrmLinkingInvitationMutation } from "./CrmLinkingInvitation.generated.js";
import { InvitationHeading } from "./InvitationHeading.js";
import { type RefTokenQuery } from "./InvitationTokenProvider.generated.js";
import { useInvitationToken } from "./useInvitationToken.js";

type Invitation = Extract<
  RefTokenQuery["crmLinkingInvitation"],
  { __typename: "CrmLinkingInvitation" }
>;

type Props = {
  invitation: Invitation;
} & (
  | {
      isAuthenticated: false;
      availableUsers?: void;
      availableUsersIsLoading?: void;
    }
  | {
      isAuthenticated: true;
      availableUsers: AvailableUser[];
      availableUsersIsLoading: boolean;
    }
);

export const CrmLinkingInvitation = ({
  invitation,
  isAuthenticated,
  availableUsers,
  availableUsersIsLoading,
}: Props) => {
  const { t } = useTranslate(["Global"]);
  const { setSelectedUserId } = useSelectedUser();
  const { token: refToken, remove: removeInvitationToken } =
    useInvitationToken();

  const [selectedUser, setSelectedUser] = React.useState<null | AvailableUser>(
    null
  );

  const prefill = React.useMemo(
    () => getCrmLinkingInvitationPrefill(invitation.contact),
    [invitation.contact]
  );

  const client = useApolloClient();
  const [acceptCrmLinkingInvitation] = useAcceptCrmLinkingInvitationMutation({
    client,
  });

  return (
    <Stack>
      {!isAuthenticated ? (
        <Stack>
          <InvitationHeading>
            {t(
              "Please sign in to connect with {inviterOrgName} on Meister Systems",
              {
                ns: "Global",
                inviterOrgName: invitation.authorOrganisation.name,
              }
            )}
          </InvitationHeading>
          <Stack spacing={1.5} sx={{ mt: "32px" }}>
            <LoginButton
              prefill={prefill}
              scope={INVITATION_SCOPE}
              sx={ONBOARDING_PRIMARY_BUTTON_SX}
            />
            <RegisterButton
              prefill={prefill}
              scope={INVITATION_SCOPE}
              variant="outlined"
              color="primary"
              sx={ONBOARDING_OUTLINED_BUTTON_SX}
            />
          </Stack>
          {refToken ? (
            <>
              <ButtonDivider label={t("Or", { ns: "Global" })} />
              {/* Keyed so another invitation starts a fresh hand-over. */}
              <ContinueOnAnotherDeviceSection
                key={refToken}
                refToken={refToken}
              />
            </>
          ) : null}
        </Stack>
      ) : (
        <Stack>
          <InvitationHeading>
            {t(
              "Please choose the organisation that will work with {inviterOrgName}",
              {
                ns: "Global",
                inviterOrgName: invitation.authorOrganisation.name,
              }
            )}
          </InvitationHeading>
          <Stack sx={{ mt: "24px" }}>
            <SelectUserList
              isLoading={availableUsersIsLoading}
              availableUsers={availableUsers}
              selectedUser={selectedUser}
              handleSelect={setSelectedUser}
            />
          </Stack>
          {invitation.onboardsExistingOrganisation ? (
            <Stack sx={{ mt: "24px" }}>
              <InfoLabelMessage
                message={t(
                  "{inviterOrgName} will onboard your craftsman organisation with its catalogues, templates and features.",
                  {
                    ns: "Global",
                    inviterOrgName: invitation.authorOrganisation.name,
                  }
                )}
              />
            </Stack>
          ) : null}
          <Stack spacing={1.5} sx={{ mt: "24px" }}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              disableElevation
              sx={ONBOARDING_PRIMARY_BUTTON_SX}
              onClick={async () => {
                if (!selectedUser)
                  throw new Error("Need to select user first!");
                await acceptCrmLinkingInvitation({
                  variables: { token: invitation.token },
                  context: {
                    headers: {
                      [SELECTED_USER_HTTP_HEADER]: selectedUser.id,
                    },
                  },
                });
                removeInvitationToken();
                setSelectedUserId(selectedUser.id);
              }}
              disabled={!selectedUser}
            >
              {t("Select", { ns: "Global" })}
            </Button>
            <Button
              fullWidth
              color="warning"
              onClick={() => {
                removeInvitationToken();
              }}
              sx={ONBOARDING_PRIMARY_BUTTON_SX}
            >
              {t("Discard", { ns: "Global" })}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
